/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Button, Box, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useNavigate, Link } from "react-router-dom";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import {
  getUserTimezone,
  opacity2hex,
  serializeDateValue,
  sortArray,
  startOfWeek,
  wrapWithAll,
} from "utils/miscUtils";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import { SimpleSelectInput } from "components/FormSelectInput";
import { transactionReportColumns } from "utils/tableHelper/reportsTableHelper";
import {
  DefaultCurrency,
  PBO_InitialValues,
  RoundStatusOptions,
  TransactionTypeOptions,
} from "utils/constants";
import {
  exportReport,
  getTransactionReportList,
  ReportTypes,
} from "services/reportsService";
import { SimpleDateInput } from "components/FormDateInput";
import { SimpleTextInput } from "components/FormTextInput";
import useFilterOptionsHook from "hooks/useFilterOptions";
import { FilterSectionProps } from "types/filters";
import UploadIcon from "@mui/icons-material/Upload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import useOpenClose from "hooks/useOpenClose";
import ExportModal from "screens/modals/ExportModal";
import { Visibility } from "@mui/icons-material";
import StatusBadge from "components/StatusBadge";
import CopyableTag from "components/CopyableTag";

// ─── Additional-filter field configs ────────────────────────────────────────
type FieldType = "text" | "number" | "enum" | "boolean";
interface FilterFieldConfig {
  value: string;
  label: string;
  type: FieldType;
  description: string;
  options?: Array<{ id: string; label: string }>;
}

const MarketTypeOptions = [
  { id: "all", label: "All" },
  { id: "FINAL_RESULT", label: "Final Result" },
  { id: "UNDER_OVER", label: "Under / Over" },
  { id: "BTTS", label: "BTTS" },
  { id: "RESULT_BTTS", label: "Result BTTS" },
  { id: "CORRECT_SCORE", label: "Correct Score" },
  { id: "FIRST_LAST_ANY_GOAL_SCORE", label: "First / Last / Any Goal" },
  { id: "HANDICAPS", label: "Handicaps" },
  { id: "FIRST_GOAL_CORRECT_SCORE", label: "First Goal + Correct Score" },
  { id: "STORE_ITEMS", label: "Store Items" },
];

const BetResolutionOptions = [
  { id: "all", label: "All" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
  { id: "void", label: "Void" },
];

const YesNoOptions = [
  { id: "all", label: "All" },
  { id: "true", label: "Yes" },
  { id: "false", label: "No" },
];

const FILTER_FIELD_CONFIGS: FilterFieldConfig[] = [
  {
    value: "roundId",
    label: "Round ID",
    type: "text",
    description:
      "Unique match/round identifier from the Game Backend. Exact match required.",
  },
  {
    value: "betId",
    label: "Internal Bet Txn ID",
    type: "text",
    description:
      "Internal RGS-generated bet transaction ID (betTxnId). Exact match required.",
  },
  {
    value: "platformTransactionId",
    label: "External Bet Ticket ID",
    type: "text",
    description:
      "External bet ticket ID assigned by the operator's platform. Exact match required.",
  },
  {
    value: "platformBetTxnId",
    label: "Platform Bet Txn ID",
    type: "text",
    description:
      "Platform's own bet transaction reference used for reconciliation.",
  },
  {
    value: "externalPlayerId",
    label: "External Player ID",
    type: "text",
    description:
      "The player's ID as registered with the operator's platform (external ID).",
  },
  {
    value: "numericRoundId",
    label: "GG Round ID (Numeric)",
    type: "number",
    description: "GamesGlobal numeric round ID.",
  },
  {
    value: "gameAlias",
    label: "Game Alias",
    type: "text",
    description:
      "Game alias/identifier string associated with a specific game variant.",
  },
  {
    value: "playerCurrencyCode",
    label: "Currency Code",
    type: "text",
    description:
      "Filter transactions by player currency code (e.g. USD, EUR, GBP).",
  },
  {
    value: "playerName",
    label: "Player Name",
    type: "text",
    description:
      "Filter by player display name. Supports partial/exact matching.",
  },
  {
    value: "playerSessionId",
    label: "Player Session ID",
    type: "text",
    description:
      "The player's active session ID at the time the bet was placed.",
  },
  {
    value: "requestItemId",
    label: "Request Item ID",
    type: "text",
    description: "Request item ID sent to the platform when placing this bet.",
  },
  {
    value: "winRequestItemId",
    label: "Win Request Item ID",
    type: "text",
    description:
      "Request item ID associated with the win payout from the platform.",
  },
  {
    value: "refundTxnId",
    label: "Refund Txn ID",
    type: "text",
    description: "Transaction ID of the refund linked to this round, if any.",
  },
  {
    value: "marketType",
    label: "Market Type",
    type: "enum",
    options: MarketTypeOptions,
    description:
      "Filter by betting market type (Final Result, Over/Under, BTTS, etc.).",
  },
  {
    value: "resolved",
    label: "Bet Status",
    type: "enum",
    options: BetResolutionOptions,
    description:
      "Filter by the final resolution of the bet: Won, Lost, or Void.",
  },
  {
    value: "isFreeBet",
    label: "Free Bet",
    type: "boolean",
    description:
      "Show only free bet transactions (Yes), exclude them (No), or show all.",
  },
  {
    value: "buyBonus",
    label: "Buy Bonus",
    type: "boolean",
    description:
      "Show only Buy Bonus transactions (Yes), exclude them (No), or show all.",
  },
];

const ADDITIONAL_FILTER_FIELD_OPTIONS = [
  { id: "", label: "— None —" },
  ...FILTER_FIELD_CONFIGS.map((f) => ({ id: f.value, label: f.label })),
];
// ────────────────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "fit-content",
    flexFlow: "row wrap",
    gap: 4,
  },
  field: {
    width: 180,
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 24,
    marginLeft: 10,
    gap: 5,
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  buttonSmall: {
    height: 28,
    fontSize: "12px",
    paddingLeft: 16,
    paddingRight: 16,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  icon: {
    [theme.breakpoints.down("sm")]: {
      fontSize: `14px !important`,
    },
  },
  amountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: theme.palette.primary.main,
  },
  winAmountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: "#4caf50",
  },
  betAmountCell: {
    fontWeight: "700 !important",
    fontSize: "13px !important",
    color: "#ff5722",
  },
  idCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40px",
  },
}));

const RowActions = ({ data, actionHandlers }: any) => {
  const targetUrl =
    configData.routes.reports.roundDetails + `?betTxnId=${data?.betTxnId}`;

  return (
    <Link
      to={targetUrl}
      style={{
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Visibility
        sx={{
          cursor: "pointer",
          color: "#1976d2",
          fontSize: "26px",
          "&:hover": {
            color: "#03053eff",
          },
        }}
      />
    </Link>
  );
};

const FilterSection = ({
  handleChange = () => {},
  onSubmitClick = () => {},
  onReset = () => {},
  filterOptions,
}: FilterSectionProps) => {
  const classes = useStyles();
  const customStyle = { height: 30 };

  const selectedFieldConfig = FILTER_FIELD_CONFIGS.find(
    (f) => f.value === filterOptions.additionalFilterField,
  );

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
    >
      {/* ── Row 1: all filters — wrap to next line when screen is too small ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: "8px",
          width: "100%",
        }}
      >
        {/* Date range */}
        <Box sx={{ minWidth: 145, flexShrink: 0 }}>
          <SimpleDateInput
            label="From:"
            id="startTime"
            name="startTime"
            fullWidth
            format="DD-MM-YYYY"
            variant="inline"
            removeBottomMargin={true}
            value={filterOptions.startTime}
            onChange={(date) =>
              handleChange({ name: "startTime", value: date })
            }
            customStyle={customStyle}
          />
        </Box>
        <Box sx={{ minWidth: 145, flexShrink: 0 }}>
          <SimpleDateInput
            label="To:"
            id="endTime"
            name="endTime"
            fullWidth
            format="DD-MM-YYYY"
            variant="inline"
            removeBottomMargin={true}
            value={filterOptions.endTime}
            onChange={(date) => handleChange({ name: "endTime", value: date })}
            customStyle={customStyle}
          />
        </Box>

        {/* Platform hierarchy */}
        <Box sx={{ minWidth: 140, flexShrink: 0 }}>
          <SimpleSelectInput
            name="platform"
            label="Platform"
            placeholdertext="Select Platforms"
            options={wrapWithAll(sessionHelper.platformOptions)}
            value={filterOptions.platform}
            onChange={(e) =>
              handleChange({ name: "platform", value: e?.target?.value })
            }
            customStyle={customStyle}
          />
        </Box>
        {![null, "all"].includes(filterOptions.platform) && (
          <Box sx={{ minWidth: 140, flexShrink: 0 }}>
            <SimpleSelectInput
              name="operator"
              label="Operator"
              options={wrapWithAll(
                sessionHelper.getFilteredOperators({
                  platformIds: [filterOptions.platform],
                }),
              )}
              value={filterOptions.operator}
              placeholder="Select Operators"
              onChange={(e) =>
                handleChange({ name: "operator", value: e?.target?.value })
              }
              customStyle={customStyle}
            />
          </Box>
        )}
        {![null, "all"].includes(filterOptions.operator) && (
          <Box sx={{ minWidth: 140, flexShrink: 0 }}>
            <SimpleSelectInput
              name="brand"
              label="Brand"
              options={wrapWithAll(
                sessionHelper.getFilteredBrands({
                  platformIds: [filterOptions.platform],
                  operatorIds: [filterOptions.operator],
                }),
              )}
              value={filterOptions.brand}
              onChange={({ target: { name, value } }) =>
                handleChange({ name, value })
              }
              customStyle={customStyle}
            />
          </Box>
        )}

        {/* Permanent enum filters */}
        <Box sx={{ minWidth: 150, flexShrink: 0 }}>
          <SimpleSelectInput
            name="transactionType"
            label="Transaction Type"
            options={TransactionTypeOptions}
            value={filterOptions.transactionType}
            onChange={({ target: { name, value } }) =>
              handleChange({ name, value })
            }
            customStyle={customStyle}
          />
        </Box>
        <Box sx={{ minWidth: 140, flexShrink: 0 }}>
          <SimpleSelectInput
            name="status"
            label="Round Status"
            options={RoundStatusOptions}
            value={filterOptions.status}
            onChange={({ target: { name, value } }) =>
              handleChange({ name, value })
            }
            customStyle={customStyle}
          />
        </Box>

        {/* Dynamic filter — field picker only (value goes to Row 2) */}
        <Box sx={{ minWidth: 180, flexShrink: 0 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: "2px" }}
          >
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1 }}
            >
              Filter By Field
            </Typography>
            <Tooltip
              title={
                selectedFieldConfig?.description ??
                "Select a field to filter transactions by a specific attribute."
              }
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 13,
                  color: selectedFieldConfig ? "primary.main" : "#bdbdbd",
                  cursor: "help",
                  transition: "color 0.2s",
                }}
              />
            </Tooltip>
          </Box>
          <SimpleSelectInput
            name="additionalFilterField"
            label=""
            options={ADDITIONAL_FILTER_FIELD_OPTIONS}
            value={filterOptions.additionalFilterField ?? ""}
            onChange={(e) =>
              handleChange({
                name: "additionalFilterField",
                value: e?.target?.value || null,
              })
            }
            customStyle={customStyle}
          />
        </Box>
      </Box>

      {/* ── Row 2: active filter value — only shown when a field is selected ── */}
      {selectedFieldConfig && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: "#f0f4ff",
            border: "1px solid #c5d5f5",
            borderRadius: 2,
            width: "fit-content",
            maxWidth: "100%",
          }}
        >
          {/* Field name badge */}
          <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "#1976d2",
                lineHeight: 1,
                mb: 0.5,
              }}
            >
              {selectedFieldConfig.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                color: "text.secondary",
                fontStyle: "italic",
                lineHeight: 1.4,
                maxWidth: 220,
              }}
            >
              {selectedFieldConfig.description}
            </Typography>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              alignSelf: "stretch",
              bgcolor: "#c5d5f5",
              flexShrink: 0,
            }}
          />

          {/* Value input */}
          <Box sx={{ minWidth: 200, flexShrink: 0 }}>
            {selectedFieldConfig.type === "enum" && (
              <SimpleSelectInput
                name="additionalFilterValue"
                label=""
                options={selectedFieldConfig.options!}
                value={filterOptions.additionalFilterValue ?? "all"}
                onChange={(e) =>
                  handleChange({
                    name: "additionalFilterValue",
                    value: e?.target?.value,
                  })
                }
                customStyle={customStyle}
              />
            )}
            {selectedFieldConfig.type === "boolean" && (
              <SimpleSelectInput
                name="additionalFilterValue"
                label=""
                options={YesNoOptions}
                value={filterOptions.additionalFilterValue ?? "all"}
                onChange={(e) =>
                  handleChange({
                    name: "additionalFilterValue",
                    value: e?.target?.value,
                  })
                }
                customStyle={customStyle}
              />
            )}
            {(selectedFieldConfig.type === "text" ||
              selectedFieldConfig.type === "number") && (
              <SimpleTextInput
                name="additionalFilterValue"
                label=""
                value={filterOptions.additionalFilterValue ?? ""}
                onChange={(e) =>
                  handleChange({
                    name: "additionalFilterValue",
                    value: e?.target?.value,
                  })
                }
                customStyle={customStyle}
                type={selectedFieldConfig.type}
              />
            )}
          </Box>
        </Box>
      )}

      {/* ── Submit / Reset — always last ── */}
      <Box sx={{ display: "flex", gap: "6px" }}>
        <Button className={classes.buttonSmall} onClick={onSubmitClick}>
          Submit
        </Button>
        <Button className={classes.buttonSmall} onClick={onReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

const TransactionReport = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const [orderDirection, setOrderDirection] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.CARD.UPDATE_ROLES,
  );
  const [columns, setColumns] = useState([]);
  const {
    filterOptions,
    onChange: setFilterOptions,
    reset,
  } = useFilterOptionsHook({
    initialValues: {
      startTime: startOfWeek(),
      endTime: new Date(),
      currency: DefaultCurrency,
      transactionType: "all",
      status: "all",
      additionalFilterField: null,
      additionalFilterValue: null,
      ...PBO_InitialValues,
    },
  });
  const { isOpen, open, close } = useOpenClose();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: list?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...list], orderDirection, column);
    setList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const buildReportPayload = () => {
    const {
      startTime,
      endTime,
      additionalFilterField,
      additionalFilterValue,
      ...rest
    } = filterOptions;
    let _start = new Date(startTime);
    let _end = new Date(endTime);
    if (_start > _end) {
      showSnackbar("Start date should not be greater than end date", "error");
      return false;
    }

    const payload: any = {
      ...rest,
      pageNo,
      pageSize,
      startTime: serializeDateValue(startTime),
      endTime: serializeDateValue(endTime),
      timezone: getUserTimezone(),
    };

    // Map the dynamic additional filter field → service param
    if (
      additionalFilterField &&
      additionalFilterValue != null &&
      additionalFilterValue !== "" &&
      additionalFilterValue !== "all"
    ) {
      payload[additionalFilterField] = additionalFilterValue;
    }

    return payload;
  };

  const fetchPlayerReport = async () => {
    setLoading(true);
    try {
      const payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const response = await getTransactionReportList(payload);
      const summary = response?.data?.transactionSummary;

      setList(summary);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  const handleFilterChange = ({ name, value }: any) => {
    // When the filter field changes, reset the value so a stale value from
    // a previous field doesn't carry over to a different field.
    if (name === "additionalFilterField") {
      setFilterOptions({ name: "additionalFilterField", value });
      setFilterOptions({ name: "additionalFilterValue", value: null });
    } else {
      setFilterOptions({ name, value });
    }
  };

  const onSendExportedDataEmail = async (email) => {
    setLoading(true);
    try {
      let payload = buildReportPayload();
      if (!payload) {
        setLoading(false);
        return;
      }

      const { platform, operator, brand, transactionType, status } = payload;

      if (platform === "all") payload.platform = null;
      if (operator === "all") payload.operator = null;
      if (brand === "all") payload.brand = null;
      if (transactionType === "all") payload.transactionType = null;
      if (status === "all") payload.status = null;

      payload = { ...payload, type: ReportTypes.txn, email };

      await exportReport(payload);

      setLoading(false);
      showSnackbar("Data request, will be sent to the email.");
    } catch (error) {
      setLoading(false);
      showSnackbar(error?.message ?? error, "error");
    }
  };

  useEffect(() => {
    const initialColumns = transactionReportColumns(filterOptions)
      .filter((col) => col.id !== "actions" || hasEditPermission)
      .map((col) => {
        // Add custom render for Internal Bet Txn ID as copyable tag
        if (col.id === "betTxnId") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.idCell}>
                <CopyableTag value={data.betTxnId} />
              </Box>
            ),
          };
        }

        // Add custom render for Round ID as copyable tag
        if (col.id === "roundId") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.idCell}>
                <CopyableTag value={data.roundId} />
              </Box>
            ),
          };
        }

        // Add custom render for bet status
        if (col.id === "resolved") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge value={data.resolved} type="resolved" />
            ),
          };
        }

        // Add custom render for round status
        if (col.id === "status") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge value={data.status} type="roundStatus" />
            ),
          };
        }

        // Add custom render for transaction type
        if (col.id === "transactionType") {
          return {
            ...col,
            renderCell: (data: any) => (
              <StatusBadge
                value={data.transactionType}
                type="transactionType"
              />
            ),
          };
        }

        // Highlight bet amount
        if (col.id === "betAmount") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.betAmountCell}>
                {data.betAmount != null ? data.betAmount.toFixed(2) : "-"}
              </Box>
            ),
          };
        }

        // Highlight win amount
        if (col.id === "winAmount") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box className={styles.winAmountCell}>
                {data.winAmount != null ? data.winAmount.toFixed(2) : "-"}
              </Box>
            ),
          };
        }

        // Bold Player Name
        if (col.id === "playerName") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box sx={{ fontWeight: "700", fontSize: "12px" }}>
                {data.playerName || "-"}
              </Box>
            ),
          };
        }

        // Boolean badge for isFreeBet
        if (col.id === "isFreeBet") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: "11px",
                  color: data.isFreeBet ? "#4caf50" : "#9e9e9e",
                }}
              >
                {data.isFreeBet === true
                  ? "Yes"
                  : data.isFreeBet === false
                    ? "No"
                    : "-"}
              </Box>
            ),
          };
        }

        // Boolean badge for buyBonus
        if (col.id === "buyBonus") {
          return {
            ...col,
            renderCell: (data: any) => (
              <Box
                sx={{
                  fontWeight: 700,
                  fontSize: "11px",
                  color: data.buyBonus ? "#1976d2" : "#9e9e9e",
                }}
              >
                {data.buyBonus === true
                  ? "Yes"
                  : data.buyBonus === false
                    ? "No"
                    : "-"}
              </Box>
            ),
          };
        }

        return col;
      });

    setColumns(initialColumns);
    fetchPlayerReport();
  }, [pageNo, pageSize]);

  return (
    <ContentShell
      title="Transactional Report"
      loading={loading}
      actions={
        <Button
          startIcon={<UploadIcon className={styles.icon} />}
          variant="contained"
          onClick={open}
        >
          Export
        </Button>
      }
    >
      <Box className={styles.playerCardParent}>
        <FilterSection
          filterOptions={filterOptions}
          handleChange={handleFilterChange}
          onSubmitClick={fetchPlayerReport}
          onReset={reset}
        />
        <Table
          columns={columns}
          data={list}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={list.length}
          highlightAltRow={true}
          actions={RowActions}
          onColumnClick={onColumnClick}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{}}
        />
      </Box>
      <ExportModal
        list={list}
        fileName="Transaction_Report"
        filterOptions={filterOptions}
        open={isOpen}
        onClose={close}
        onSendEmail={onSendExportedDataEmail}
      />
    </ContentShell>
  );
};

export default TransactionReport;
